import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from "@apollo/client";
import {
  Building2,
  Calculator,
  Search,
  Shield,
  User as UserIcon,
} from "lucide-react";
import { PlusIcon } from "lucide-react";
import { useQueryState } from "nuqs";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AccountingFirmLayout } from "@/components/layouts/accounting-firm-layout";
import { CompanyAdminOnly } from "@/components/shared/miscellaneous/companyadmin-only";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { TypographyMuted, TypographySmall } from "@/components/ui/typography";
import { USER_COMPANIES_QUERY } from "@/queries/companies/get-all";
import { NavUser } from "@/components/shared/app-navigation/navbar/nav-user";
import { CreateUserModal } from "@/features/users/create-user/create-user-modal";
import { EditUserModal } from "@/features/users/edit-user/edit-user-modal";
import { CompanyRole } from "@/lib/miscellaneous/authorization";
import {
  getHighestUserCompanyRole,
  getUserAccountingFirmRole,
} from "@/lib/users/user-roles";
import { useUsers } from "@/queries/users/get-all";
import {
  type UserWithAccountingFirmsAndCompanyRoles,
} from "@/types/user";
import PageTitle from "@/components/shared/page-layout/PageTitle";

export const Route = createFileRoute('/_authenticated/kanzlei/nutzer')({
  component: UsersPage,
})

function UsersPage() {
  const [users, setUsers] = useState<UserWithAccountingFirmsAndCompanyRoles[]>(
    [],
  );
  const { t } = useTranslation();
  const [search, setSearch] = useQueryState("q", { defaultValue: "" });
  const [isNewUserModalOpen, setIsNewUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] =
    useState<UserWithAccountingFirmsAndCompanyRoles | null>(null);
  const [filteredUsers, setFilteredUsers] = useState<
    UserWithAccountingFirmsAndCompanyRoles[]
  >([]);
  const { users: userData } = useUsers(false, false, true);

  const { data: companiesData } = useQuery(USER_COMPANIES_QUERY);

  useEffect(() => {
    if (userData) {
      setUsers(userData);
    }
  }, [userData]);

  useEffect(() => {
    let filteredUsers: UserWithAccountingFirmsAndCompanyRoles[] = users;
    if (search)
      filteredUsers =
        users.filter(
          (user) =>
            user.name.toLowerCase().includes(search.toLowerCase()) ||
            user.email.toLowerCase().includes(search.toLowerCase()),
        ) ?? [];
    setFilteredUsers(filteredUsers);
  }, [search, users]);

  const getUserInitials = (name: string) => {
    if (!name) return "?";
    const parts = name.split(" ");
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase();
  };

  const handleUserClick = (user: UserWithAccountingFirmsAndCompanyRoles) => {
    setSelectedUser(user);
    setIsEditUserModalOpen(true);
  };

  const getCompanyCountForUser = (userId: string) => {
    if (!companiesData?.userCompanies) return 0;
    return companiesData.userCompanies.filter((company: any) =>
      company.users?.some((u: any) => u.userId === userId),
    ).length;
  };

  const renderUserRole = (user: UserWithAccountingFirmsAndCompanyRoles) => {
    const isAccountingFirmUser =
      user.accountingFirms && user.accountingFirms.length > 0;
    if (isAccountingFirmUser) {
      if (getUserAccountingFirmRole(user) === "admin") {
        return (
          <>
            <Shield className="h-4 w-4" />
            {t("role.admin")}
          </>
        );
      }
      return (
        <>
          <UserIcon className="h-4 w-4" />
          {t("role.user")}
        </>
      );
    }
    if (getHighestUserCompanyRole(user) === CompanyRole.CLIENT_SELF_BOOKING) {
      return (
        <>
          <Calculator className="h-4 w-4" />
          {t("users.type_client_self_booking")}
        </>
      );
    }
    return (
      <>
        <Building2 className="h-4 w-4" />
        {t("users.type_client")}
      </>
    );
  };

  return (
    <>
      <div className="flex flex-col h-screen">
        <PageTitle location={[t("accounting-firm"), t("users.users")]}>
          <div className="flex flex-row gap-2 items-center">
            <NavUser />
          </div>
        </PageTitle>
        <div className="flex flex-row items-center gap-2 py-2 px-4">
          <div className="relative w-96">
            <Input
              placeholder={t("users.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-7 w-full"
            />
            <Search
              width={16}
              height={16}
              className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 select-none opacity-50"
            />
          </div>
          <div className="flex-1" />
          <Button onClick={() => setIsNewUserModalOpen(true)} className="mr-2">
            <PlusIcon className="mr-2 h-4 w-4" />
            {t("users.createUser")}
          </Button>
        </div>
        <div className="flex flex-row gap-2 py-1 px-4 items-center">
          <div className="flex-1 px-2">
            <TypographyMuted>{t("users.name")}</TypographyMuted>
          </div>

          <div className="flex-1 px-2">
            <TypographyMuted>{t("users.email")}</TypographyMuted>
          </div>
          <div className="w-40 px-2 flex-shrink-0">
            <TypographyMuted>{t("users.accesses")}</TypographyMuted>
          </div>
          <div className="flex-1 px-2">
            <TypographyMuted>{t("users.role")}</TypographyMuted>
          </div>

          <div className="w-24 px-2 flex-shrink-0"></div>
        </div>
        <div className="px-4 mt-1">
          <Separator />
        </div>
        <div className="flex-1 px-4 pb-4 overflow-y-auto relative">
          <ul className="grid grid-cols-1 gap-0">
            {filteredUsers.map((user, index) => {
              const companyCount = getCompanyCountForUser(user.userId);
              return (
                <li
                  key={user.userId}
                  className="col-span-1 flex hover:bg-muted"
                >
                  <div className="w-full">
                    <div
                      className="flex flex-row items-center space-x-2 p-3 cursor-pointer"
                      onClick={() => handleUserClick(user)}
                    >
                      <div className="flex-1 flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>
                            {getUserInitials(user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <TypographySmall>{user.name}</TypographySmall>
                      </div>

                      <div className="flex-1">
                        <TypographySmall>{user.email}</TypographySmall>
                      </div>
                      <div className="w-40 flex-shrink-0 flex">
                        <span className="bg-gray-100 text-gray-700 rounded-full px-3 py-1 text-sm font-medium">
                          {t("users.mandate_count", { count: companyCount })}
                        </span>
                      </div>
                      <div className="flex-1">
                        <TypographySmall className="flex items-center gap-2">
                          {renderUserRole(user)}
                        </TypographySmall>
                      </div>

                      <div className="w-24 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUserClick(user)}
                        >
                          {t("users.edit")}
                        </Button>
                      </div>
                    </div>
                    <Separator />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <CreateUserModal
        open={isNewUserModalOpen}
        onOpenChange={setIsNewUserModalOpen}
      />
      <EditUserModal
        open={isEditUserModalOpen}
        onOpenChange={setIsEditUserModalOpen}
        user={selectedUser}
      />
    </>
  );
}
